/**
 * `requireFeatureEnabled` — loader paramétré qui coupe l'accès à une zone de
 * routes tant qu'une feature n'est pas prête pour une exposition utilisateur
 * inconditionnelle.
 *
 * @remarks
 * Se déclare sur le groupe de routes d'une feature, comme `requireRole` :
 *
 * ```ts
 * routes: [{ routes: referralRoutes, guard: requireFeatureEnabled(false) }]
 * ```
 *
 * **`enabledWhen` ne suffit pas à protéger des routes** (cf. le remarks de
 * `FeatureRouteSet.guard` dans `registry/types.ts`, SAAS-883) : il ne filtre
 * que ce que le shell affiche — vue centrale, onglets, lien de nav —
 * `createRouter()` monte les routes de **toutes** les features enregistrées
 * sans le consulter. Une URL tapée à la main atteint donc la route dans tous
 * les cas ; c'est le `guard` qui répond.
 *
 * Contrairement à `requireRole`, la condition n'est pas fonction de
 * l'utilisateur courant — elle est arbitrée en amont, à l'appel (feature pas
 * encore stabilisée côté produit, cf. SAAS-1046). L'utilisateur authentifié
 * qui tape l'URL est renvoyé vers l'accueil applicatif, comme pour une zone
 * réservée par rôle : pas de page de refus dédiée qui confirmerait que la
 * route existe.
 */

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth } from './require-auth';

/** Où atterrit un utilisateur authentifié à qui la zone est refusée. */
const FALLBACK_ROUTE = '/tests';

export function requireFeatureEnabled(enabled: boolean) {
  return async (args: LoaderFunctionArgs): Promise<null | Response> => {
    const authRedirect = await requireAuth(args);
    if (authRedirect) return authRedirect;

    return enabled ? null : redirect(FALLBACK_ROUTE);
  };
}
